import type { ApiError } from '@shared/types';
import { config } from '@shared/config';
import { normalizeError } from './normalizeError';

export type QueryParams = Record<string, string | string[]>;

export interface ApiClient {
  get<T>(url: string, params?: QueryParams): Promise<T>;
  post<T>(url: string, body?: unknown): Promise<T>;
  postFormData<T>(url: string, formData: FormData): Promise<T>;
  put<T>(url: string, body?: unknown): Promise<T>;
  patch<T>(url: string, body?: unknown): Promise<T>;
  delete<T>(url: string): Promise<T>;
  getBlob(url: string, params?: QueryParams): Promise<Blob>;
}

export class ApiRequestError extends Error {
  constructor(public readonly apiError: ApiError) {
    super(apiError.message);
    this.name = 'ApiRequestError';
  }
}

export class FetchApiClient implements ApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? config.apiBaseUrl;
  }

  async get<T>(url: string, params?: QueryParams): Promise<T> {
    let query = '';
    if (params) {
      const qs = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          for (const v of value) qs.append(key, v);
        } else {
          qs.append(key, value);
        }
      }
      query = `?${qs.toString()}`;
    }
    return this.request<T>(`${url}${query}`, { method: 'GET' });
  }

  async post<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    // No Content-Type header — browser sets it with the multipart boundary.
    return this.request<T>(url, { method: 'POST', body: formData });
  }

  async put<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/merge-patch+json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(url: string): Promise<T> {
    return this.request<T>(url, { method: 'DELETE' });
  }

  async getBlob(url: string, params?: QueryParams): Promise<Blob> {
    let query = '';
    if (params) {
      const qs = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          for (const v of value) qs.append(key, v);
        } else {
          qs.append(key, value);
        }
      }
      query = `?${qs.toString()}`;
    }
    const token = this.getAuthToken();
    const fullUrl = `${this.baseUrl}${url}${query}`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const apiError = await normalizeError(response);
      throw new ApiRequestError(apiError);
    }
    return response.blob();
  }

  private getAuthToken(): string | null {
    try {
      const raw = localStorage.getItem('oms-auth');
      if (raw) {
        const parsed = JSON.parse(raw) as { state?: { tokens?: { accessToken?: string } } };
        const token = parsed?.state?.tokens?.accessToken;
        if (token) return token;
      }
    } catch { /* ignore */ }

    try {
      const oidcKey = `oidc.user:${config.oidcAuthority}:${config.oidcClientId}`;
      const raw = localStorage.getItem(oidcKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { access_token?: string };
        if (parsed?.access_token) return parsed.access_token;
      }
    } catch { /* ignore */ }

    return null;
  }

  private async request<T>(url: string, init: RequestInit): Promise<T> {
    const token = this.getAuthToken();
    const fullUrl = `${this.baseUrl}${url}`;
    const response = await fetch(fullUrl, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      const apiError = await normalizeError(response);
      throw new ApiRequestError(apiError);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }
}

/** Default singleton API client instance. */
export const apiClient: ApiClient = new FetchApiClient();
